/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */
import { ApiPersonDetailReviewsListServiceTypeCategory as ServiceTypeCategories } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListServiceTypeCategory';
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

import type { BundleOptionValue } from '../constants/bundleTemplates';

abstract class TrainingEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.service_offerings.training;
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/provider-profile/services/dog-training/` on page load.
 *
 * &nbsp;
 */
export class TrainingServiceFormPageLoad extends TrainingEvent {
  eventName = 'training-service-form-page-load';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/provider-profile/services/dog-training/` after a user
 * updates their service preferences for their training service.
 *
 * &nbsp;
 */
export class TrainingServiceFormSubmitSuccess extends TrainingEvent {
  eventName = 'training-service-form-submit-success';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/provider-profile/services/dog-training/` when a user clicks the
 * "Add bundle" button, opening the add bundle modal.
 *
 * &nbsp;
 */
export class TrainingServiceFormAddBundleClicked extends TrainingEvent {
  eventName = 'training-service-form-add-bundle-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/provider-profile/services/dog-training/` when a user picks a
 * bundle template or "Create your own" on the add bundle interstitial.
 *
 * &nbsp;
 */
export class TrainingAddBundleInterstitialSubmit extends TrainingEvent {
  eventName = 'training-add-bundle-interstitial-submit';

  constructor(eventProperties: { optionValue: BundleOptionValue }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/provider-profile/services/dog-training/` when a user saves the
 * add bundle form and the bundle is created. `prefilledName` is the template
 * name the form was opened with, or `null` when the bundle was built from a
 * blank form.
 *
 * &nbsp;
 */
export class TrainingAddBundleSave extends TrainingEvent {
  eventName = 'training-add-bundle-save';

  constructor(eventProperties: {
    prefilledName: string | null;
    finalName: string;
    sessionCount: number;
    ratePerSession: number;
    totalCost: number;
    sessionsEdited: boolean;
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when the Trainer Public Profile Page loads, with
 * consent purpose 'marketing', for optimizing ad spend.
 *
 * &nbsp;
 */
export class TrainerPublicProfilePageView extends TrainingEvent {
  eventName = 'trainer-public-profile-page-view';

  consentPurpose: ConsentPurpose = 'marketing';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{member_slug}/` on page load, reporting which of
 * the trainer's optional profile sections have content to display.
 *
 * &nbsp;
 */
export class TrainerPublicProfilePageOptionalSectionsShown extends TrainingEvent {
  eventName = 'trainer-public-profile-page-optional-sections-shown';

  constructor(eventProperties: {
    methodologyDetailsPresent: boolean;
    availabilityDetailsPresent: boolean;
    consultationDetailsPresent: boolean;
    singleSessionDetailsPresent: boolean;
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/provider-profile/training-details` after a user
 * updates their trainer profile details.
 *
 * &nbsp;
 */
export class TrainerProfileDetailsFormSubmitSuccess extends TrainingEvent {
  eventName = 'trainer-profile-details-form-submit-success';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/provider-profile/training-education` after a user
 * updates their trainer education details.
 *
 * &nbsp;
 */
export class TrainerProfileEducationFormSubmitSuccess extends TrainingEvent {
  eventName = 'trainer-profile-education-form-submit-success';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/search` when the page loads successfully with results.
 *
 * &nbsp;
 */
export class TrainerSearchPageResults extends TrainingEvent {
  eventName = 'trainer-search-page-results';

  constructor(eventProperties: {
    resultCount: number;
    virtualCount: number;
    searchQueryLocation?: string;
    userGeoLocation?: string;
    usedAutoLocation: boolean;
    skillsAndBehaviors: {
      skills: string[];
      behaviors: string[];
    };
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/search` and `/training/members/*` when the
 * Supported Methodologies modal loads.
 *
 * &nbsp;
 */
export class TrainingMethodologyModalLoads extends TrainingEvent {
  eventName = 'training-methodology-modal-loads';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{member_slug}/contact` when the Trainer Contact
 * Form loads. This is an event to differentiate from when the same page loads
 * on the booking experience.
 *
 * &nbsp;
 */

export class TrainerContactFormLoads extends TrainingEvent {
  eventName = 'trainer-contact-form-loads';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{member_slug}/contact` when the contact form
 * is submitted and counts the number of skills selected.
 *
 * &nbsp;
 */

export class TrainerContactSubmitSkillsCount extends TrainingEvent {
  eventName = 'trainer-contact-submit-skills-count';

  constructor(eventProperties: { skillsCount: number }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{member_slug}/contact` when the contact form
 * is submitted and counts the number of behaviors selected.
 *
 * &nbsp;
 */

export class TrainerContactSubmitBehaviorsCount extends TrainingEvent {
  eventName = 'trainer-contact-submit-behaviors-count';

  constructor(eventProperties: { behaviorsCount: number }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * Fires on `/training/members/{member_slug}/contact` when send
 * request is clicked
 *
 * &nbsp;
 */
export class TrainerRequestASessionSendRequest extends TrainingEvent {
  eventName = 'trainer-request-a-session-send-request';

  constructor(eventProperties: { sessionType?: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * Fires on `/training/account/conversations/{opk}/modify` when send
 * cancel is clicked.
 *
 * &nbsp;
 */
export class TrainingModifyRequestCancel extends TrainingEvent {
  eventName = 'training-modify-request-cancel';
}

/**
 * &nbsp;
 *

 * Fires on `/training/account/conversations/{opk}/modify` when send
 * submit changes is clicked. This can only happen if a detail has
 * been edited.
 *
 * &nbsp;
 */
export class TrainingModifyRequestSubmit extends TrainingEvent {
  eventName = 'training-modify-request-submit';

  constructor(eventProperties: { sessionType?: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/account/conversations/{opk}/cancel-cancel` when
 * Return to conversation is clicked.
 *
 * &nbsp;
 */
export class TrainingCancelBookingReturnToConversationClicked extends TrainingEvent {
  eventName = 'training-cancel-booking-return-to-conversation-clicked';
}

/**
 * &nbsp;
 *
 * Fires on `/training/account/conversations/{opk}/cancel-cancel` when
 * Cancel booking is clicked.
 *
 * &nbsp;
 */
export class TrainingCancelBookingCancelClicked extends TrainingEvent {
  eventName = 'training-cancel-booking-cancel-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/trainer-reviews/add?ttr_token={token} when the
 * review is submitted.
 *
 * &nbsp;
 */
export class TrainerAddAReviewSubmitted extends TrainingEvent {
  eventName = 'trainer-add-a-review-submitted';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{provider-slug}/contact/{user-slug} when the
 * page loads
 *
 * &nbsp;
 */
export class TrainerRebookPageLoad extends TrainingEvent {
  eventName = 'trainer-rebook-page-load';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{provider-slug}/contact/{user-slug} when the
 * page is submitted
 *
 * &nbsp;
 */
export class TrainerRebookPageSubmit extends TrainingEvent {
  eventName = 'trainer-rebook-page-submit';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{member_slug}/` when the
 * Contact button is clicked.
 *
 * &nbsp;
 */
export class TrainerProfileContactButtonClicked extends TrainingEvent {
  eventName = 'trainer-profile-contact-button-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{member_slug}/` when the reviews service type
 * filter is changed.
 *
 * &nbsp;
 */
export class TrainerProfileReviewsFilterChanged extends TrainingEvent {
  eventName = 'trainer-profile-reviews-filter-changed';

  constructor(eventProperties: { serviceTypeCategory: ServiceTypeCategories }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{member_slug}/` when the
 * "Read more reviews" button is clicked, bringing up the reviews modal.
 *
 * &nbsp;
 */
export class TrainerProfileReadAllReviewsClicked extends TrainingEvent {
  eventName = 'trainer-profile-read-all-reviews-clicked';

  constructor(eventProperties: { providerOpk?: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{member_slug}/` when the
 * "(X reviews on Rover)" text is clicked, bringing up the reviews modal.
 *
 * &nbsp;
 */
export class TrainerProfileReadAllReviewsClickedTop extends TrainingEvent {
  eventName = 'trainer-profile-read-all-reviews-clicked-top';

  constructor(eventProperties: { providerOpk?: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{member_slug}/` when a review's "Read more"
 * link is clicked.
 *
 * &nbsp;
 */
export class TrainerProfileReviewClicked extends TrainingEvent {
  eventName = 'trainer-profile-review-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/credentials-upload/` when a trainer submits
 * their credentials.
 *
 * &nbsp;
 */
export class TrainingSignUpCertUploadSelect extends TrainingEvent {
  eventName = 'training-sign-up-certupload-select';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/credentials-upload/` when a trainer backs out of
 * uploading by hitting the "Skip for now" button.
 *
 * &nbsp;
 */
export class TrainingSignUpCertCancelSelect extends TrainingEvent {
  eventName = 'training-sign-up-certcancel-select';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/credentials-upload/` when a trainer selects the "Try again"
 * button after receiving a submission error.
 *
 * &nbsp;
 */
export class TrainingSignUpCertTryAgainError extends TrainingEvent {
  eventName = 'training-sign-up-certtryagain-error';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/provider-profile/` when a trainer clicks the "Upload credentials"
 * button on the training credentials upload banner, which opens the upload page.
 *
 * &nbsp;
 */
export class TrainingSignUpCertUploadStartSelect extends TrainingEvent {
  eventName = 'training-sign-up-certuploadstart-select';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/members/{member_slug}/` when the
 * "What do I need for virtual training" button is clicked, bringing up the virtual training steps modal.
 *
 * &nbsp;
 */
export class TrainerProfileVirtualTrainingBoxClicked extends TrainingEvent {
  eventName = 'trainer-profile-virtual-training-box-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/search` when a section of the FAQ is open or closed.
 *
 * &nbsp;
 */
export class TrainingFaqSection extends TrainingEvent {
  eventName = 'training-faq-section';

  constructor(eventProperties: { question: string; isOpen: boolean }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/search` when the Back to top button is clicked
 *
 * &nbsp;
 */
export class TrainerSearchBackToTopButtonClicked extends TrainingEvent {
  eventName = 'trainer-search-back-to-top-button-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when the "Learn more" link is clicked
 *
 * &nbsp;
 */
export class TrainerSearchHeaderLearnMoreClicked extends TrainingEvent {
  eventName = 'trainer-search-header-learn-more-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when the "Read more" link is clicked in the FAQ preview banner
 *
 * &nbsp;
 */
export class TrainerSearchFaqPreviewReadMoreClicked extends TrainingEvent {
  eventName = 'trainer-search-faq-preview-read-more-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/search` when the Service Interest modal first becomes visible.
 *
 * &nbsp;
 */
export class TrainingSearchServiceInterestLoad extends TrainingEvent {
  eventName = 'training-search-serviceinterest-load';

  constructor(eventProperties: { searchQueryLocation?: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/search` when the user submits or dismisses the Service Interest modal.
 *
 * &nbsp;
 */
export class TrainingSearchServiceInterestSubmit extends TrainingEvent {
  eventName = 'training-search-serviceinterest-submit';

  constructor(eventProperties: {
    searchQueryLocation?: string;
    selectedSessionTypes?: string[];
    dismissed: boolean;
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when the "Learn more" button is clicked on the trainer recruitment widget
 *
 * &nbsp;
 */
export class TrainerRecruitmentWidgetLearnMoreClicked extends TrainingEvent {
  eventName = 'trainer-recruitment-widget-learn-more-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when the "No thanks" button is clicked on the trainer recruitment widget
 *
 * &nbsp;
 */
export class TrainerRecruitmentWidgetDismissClicked extends TrainingEvent {
  eventName = 'trainer-recruitment-widget-dismiss-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when the "Learn more" button is clicked on the bundle offering promotion widget
 *
 * &nbsp;
 */
export class BundleOfferingPromotionWidgetLearnMoreClicked extends TrainingEvent {
  eventName = 'bundle-offering-promotion-widget-learn-more-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when the "No thanks" button is clicked on the bundle offering promotion widget
 *
 * &nbsp;
 */
export class BundleOfferingPromotionWidgetDismissClicked extends TrainingEvent {
  eventName = 'bundle-offering-promotion-widget-dismiss-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user clicks on the "Show more trainers in your area" link on
 * the public trainer profile page
 *
 * &nbsp;
 */
export class ShowMoreTrainersLinkClicked extends TrainingEvent {
  eventName = 'trainer-profile-seo-back-to-search';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user clicks on the "Yes, send me the info" button on the
 * the certification requirement page.
 *
 * &nbsp;
 */
export class TrainingSignUpCredentialInterestSendInfo extends TrainingEvent {
  eventName = 'training-sign-up-credential-interest-send-info';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user clicks on the "No thanks" button on the
 * the certification requirement page.
 *
 * &nbsp;
 */
export class TrainingSignUpCredentialNoInterest extends TrainingEvent {
  eventName = 'training-sign-up-credential-no-interest';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user clicks on the "Not yet, but I'm interested" button on the
 * the sign up intro page.
 *
 * &nbsp;
 */
export class TrainingSignUpGatingCredentialInterest extends TrainingEvent {
  eventName = 'training-sign-up-gating-credential-interest';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a user clicks the "Learn More" button on the Other services section on our trainer landing page.
 *
 * &nbsp;
 */
export class TrainingSignUpOtherServicesClick extends TrainingEvent {
  eventName = 'training-sign-up-other-services-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a user visits /become-a-trainer with the query param directContact set to true.
 * This parameter is present when Rover directly contacted the user to invite them to be come a trainer
 *
 * &nbsp;
 */
export class TrainingSignUpDirectContactVisit extends TrainingEvent {
  eventName = 'trainer-sign-up-direct-contact';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when the training modify booking page loads with the
 * `?add_consultation=true` query parameter (provider view only).
 *
 * &nbsp;
 */
export class AddConsultationClicked extends TrainingEvent {
  eventName = 'add-consultation-clicked';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a provider submits a modify booking request from
 * the "Add a consultation" alternate view (`?add_consultation=true`).
 *
 * &nbsp;
 */
export class AddConsultationRequestSubmitted extends TrainingEvent {
  eventName = 'add-consultation-request-submitted';

  constructor(eventProperties: { sessionType?: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/provider-profile/services/dog-training/` when a user expands the
 * `BundleInsightBlock` to read more about how bundles work on Rover.
 *
 * &nbsp;
 */
export class TrainingSignUpBundleInsightExpand extends TrainingEvent {
  eventName = 'training-sign-up-bundle-insight-expand';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/contact-more-trainers/{conversation_opk}/` when either the
 * recommended trainer's review count or the review snippet's "See all" button is
 * clicked, bringing up the reviews modal.
 *
 * &nbsp;
 */
export class TrainingCmtPageReviewModal extends TrainingEvent {
  eventName = 'training-cmt-page-review-modal';

  constructor(eventProperties: { providerOpk: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/contact-more-trainers/{conversation_opk}/` when a photo in the
 * recommended trainer's gallery is clicked, opening it full screen.
 *
 * &nbsp;
 */
export class TrainingCmtPagePhotoGallery extends TrainingEvent {
  eventName = 'training-cmt-page-photo-gallery';

  constructor(eventProperties: { providerOpk: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/contact-more-trainers/<conversation opk>` when a user opens the
 * session options modal from the recommended trainer card's sessions section.
 *
 * &nbsp;
 */
export class TrainingCmtPageSessionOptions extends TrainingEvent {
  eventName = 'training-cmt-page-session-options';

  constructor(eventProperties: { providerOpk: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on `/training/contact-more-trainers/{conversation_opk}/` when the owner
 * clicks the contact button on the recommended trainer card, opening the session
 * type selector modal.
 *
 * &nbsp;
 */
export class TrainingCmtPageSubmit extends TrainingEvent {
  eventName = 'training-cmt-page-submit';

  constructor(eventProperties: { providerOpk: string }) {
    super(eventProperties);
  }
}
